import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import url from '../constants/config';
import Loader from '../shared/displayComponents/loader/Loader';

const getAuthHeader = () => ({
  Authorization: `Bearer ${window.localStorage.getItem('authorization')}`,
});

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#475569',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 12px',
  verticalAlign: 'middle',
  fontSize: '14px',
};

const btnStyle = {
  padding: '5px 12px',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: 600,
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatDuration = mins => {
  if (!mins || mins <= 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}min`;
  if (h > 0) return `${h}h`;
  return `${m}min`;
};

const StatusBadge = ({ status }) => {
  const map = {
    pending: { label: 'Oczekuje', bg: '#fef3c7', color: '#92400e', icon: '⏳' },
    approved: { label: 'Zatwierdzone', bg: '#dcfce7', color: '#166534', icon: '✓' },
    rejected: { label: 'Odrzucone', bg: '#fee2e2', color: '#991b1b', icon: '✗' },
  };
  const s = map[status] || map.pending;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '12px',
        fontWeight: 600,
        background: s.bg,
        color: s.color,
        whiteSpace: 'nowrap',
      }}
    >
      {s.icon} {s.label}
    </span>
  );
};


const AdminTimeEntries = () => {
  const [month, setMonth] = useState(currentMonth());
  const [assistantEmail, setAssistantEmail] = useState('');
  // Default to 'pending' so admin immediately sees what needs action
  const [statusFilter, setStatusFilter] = useState('pending');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rejectId, setRejectId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Bulk approve state
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Payroll toggle: when true, summary only counts approved entries
  const [approvedOnly, setApprovedOnly] = useState(false);

  // Detail modal
  const [detailEntry, setDetailEntry] = useState(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    setSelectedIds(new Set());
    try {
      const params = {};
      if (month) params.month = month;
      if (assistantEmail) params.assistantEmail = assistantEmail;
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${url}/admin/time-entries`, {
        headers: getAuthHeader(),
        params,
      });
      setData(res.data);
    } catch (err) {
      setError('Nie udało się załadować wpisów.');
    } finally {
      setLoading(false);
    }
  }, [month, assistantEmail, statusFilter]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Pending count from all entries (regardless of current status filter)
  const [pendingCount, setPendingCount] = useState(null);
  useEffect(() => {
    // Fetch pending count separately (no status filter) just for the badge
    const fetchPending = async () => {
      try {
        const params = {};
        if (month) params.month = month;
        if (assistantEmail) params.assistantEmail = assistantEmail;
        params.status = 'pending';
        const res = await axios.get(`${url}/admin/time-entries`, {
          headers: getAuthHeader(),
          params,
        });
        setPendingCount(res.data.entries.length);
      } catch {
        // silently ignore
      }
    };
    fetchPending();
  }, [month, assistantEmail]);

  const handleApprove = async id => {
    setActionLoading(true);
    try {
      const res = await axios.patch(
        `${url}/admin/time-entries/${id}/approve`,
        {},
        { headers: getAuthHeader() },
      );
      setData(prev => ({
        ...prev,
        entries: prev.entries.map(e => (e._id === id ? res.data : e)),
      }));
      setPendingCount(prev => (prev !== null ? Math.max(0, prev - 1) : null));
    } catch {
      setError('Nie udało się zatwierdzić wpisu.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectSubmit = async id => {
    setActionLoading(true);
    try {
      const res = await axios.patch(
        `${url}/admin/time-entries/${id}/reject`,
        { note: rejectNote },
        { headers: getAuthHeader() },
      );
      setData(prev => ({
        ...prev,
        entries: prev.entries.map(e => (e._id === id ? res.data : e)),
      }));
      setRejectId(null);
      setRejectNote('');
    } catch {
      setError('Nie udało się odrzucić wpisu.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      await axios.patch(
        `${url}/admin/time-entries/bulk-approve`,
        { ids: [...selectedIds] },
        { headers: getAuthHeader() },
      );
      await fetchEntries();
      setPendingCount(prev =>
        prev !== null ? Math.max(0, prev - selectedIds.size) : null,
      );
    } catch {
      setError('Nie udało się zatwierdzić wybranych wpisów.');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelect = id => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const approvable = (data?.entries || []).filter(e => e.status !== 'approved');
    if (selectedIds.size === approvable.length && approvable.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(approvable.map(e => e._id)));
    }
  };

  const assistants = data
    ? [...new Set((data.summary?.byAssistant || []).map(r => r._id))].sort()
    : [];

  // Summary — uses server-side aggregation (all statuses for the month)
  // approvedOnly toggle switches between full summary and approved-only summary
  const summarySource = useMemo(() => {
    if (!data) return null;
    return approvedOnly ? data.approvedSummary : data.summary;
  }, [data, approvedOnly]);

  const summaryByAssistant = summarySource?.byAssistant || [];
  const summaryByPlayer = summarySource?.byPlayer || [];

  const approvableEntries = data
    ? data.entries.filter(e => e.status !== 'approved')
    : [];
  const allApprovableSelected =
    approvableEntries.length > 0 &&
    approvableEntries.every(e => selectedIds.has(e._id));

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1400px' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          marginBottom: '20px',
          flexWrap: 'wrap',
        }}
      >
        <h2 style={{ margin: 0, color: '#1e293b', fontSize: '22px', fontWeight: 700 }}>
          Wpisy Czasu Pracy
        </h2>
        {pendingCount !== null && pendingCount > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              background: '#fef3c7',
              color: '#92400e',
              border: '1px solid #fde68a',
              borderRadius: '999px',
              padding: '3px 12px',
              fontSize: '13px',
              fontWeight: 700,
              cursor: 'pointer',
            }}
            onClick={() => setStatusFilter('pending')}
            title="Pokaż oczekujące"
          >
            <span role="img" aria-label="oczekuje">⏳</span> {pendingCount} oczekuje zatwierdzenia
          </span>
        )}
        {pendingCount === 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              background: '#dcfce7',
              color: '#166534',
              border: '1px solid #bbf7d0',
              borderRadius: '999px',
              padding: '3px 12px',
              fontSize: '13px',
              fontWeight: 600,
            }}
          >
            ✓ Wszystko zatwierdzone
          </span>
        )}
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: '12px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '24px',
          padding: '16px',
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
        }}
      >
        <div>
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: '#64748b',
              marginBottom: '4px',
            }}
          >
            Miesiąc
          </label>
          <input
            type="month"
            value={month}
            onChange={e => setMonth(e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          />
        </div>
        <div>
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: '#64748b',
              marginBottom: '4px',
            }}
          >
            Asystentka
          </label>
          <select
            value={assistantEmail}
            onChange={e => setAssistantEmail(e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '14px',
              minWidth: '180px',
            }}
          >
            <option value="">Wszystkie</option>
            {assistants.map(a => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            style={{
              display: 'block',
              fontSize: '11px',
              fontWeight: 600,
              color: '#64748b',
              marginBottom: '4px',
            }}
          >
            Status
          </label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{
              padding: '6px 10px',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '14px',
            }}
          >
            <option value="">Wszystkie</option>
            <option value="pending">Oczekujące</option>
            <option value="approved">✓ Zatwierdzone</option>
            <option value="rejected">✗ Odrzucone</option>
          </select>
        </div>
      </div>

      {error && (
        <div
          style={{
            color: '#dc2626',
            background: '#fee2e2',
            padding: '10px 16px',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}

      {loading && <Loader />}

      {/* Detail modal */}
      {detailEntry && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}
          onClick={() => setDetailEntry(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              width: '520px',
              maxWidth: '92vw',
              maxHeight: '85vh',
              overflow: 'auto',
              padding: '28px 32px',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                Szczegoly wpisu
              </h3>
              <button
                type="button"
                onClick={() => setDetailEntry(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '22px',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  lineHeight: 1,
                  padding: '0 4px',
                }}
              >
                &times;
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              <StatusBadge status={detailEntry.status} />
              {detailEntry.status === 'rejected' && detailEntry.adminNote && (
                <span style={{ fontSize: '13px', color: '#991b1b' }}>
                  {detailEntry.adminNote}
                </span>
              )}
            </div>

            {[
              ['Asystentka', detailEntry.assistantEmail],
              ['Zawodnik', detailEntry.playerName],
              ['Data', detailEntry.date],
              ['Godziny', `${detailEntry.startTime} – ${detailEntry.endTime}`],
              ['Czas', formatDuration(detailEntry.durationMinutes)],
              ['Stawka', detailEntry.hourlyRate != null ? `${detailEntry.hourlyRate} PLN/h` : '—'],
              ['Koszt', detailEntry.totalPay != null ? `${detailEntry.totalPay.toFixed(2)} PLN` : '—'],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  padding: '8px 0',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <span style={{ width: '120px', flexShrink: 0, fontWeight: 600, color: '#475569', fontSize: '13px' }}>
                  {label}
                </span>
                <span style={{ color: '#1e293b', fontSize: '14px' }}>{value}</span>
              </div>
            ))}

            {/* Notes — full display */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontWeight: 600, color: '#475569', fontSize: '13px', marginBottom: '6px' }}>
                Notatki
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  color: '#1e293b',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.6,
                  minHeight: '48px',
                }}
              >
                {detailEntry.notes || <span style={{ color: '#cbd5e1' }}>Brak notatek</span>}
              </div>
            </div>

            {/* Actions in modal */}
            <div style={{ marginTop: '24px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {detailEntry.status !== 'approved' && (
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => {
                    handleApprove(detailEntry._id);
                    setDetailEntry(prev => ({ ...prev, status: 'approved' }));
                  }}
                  style={{ ...btnStyle, background: '#dcfce7', color: '#166534', padding: '8px 18px', fontSize: '13px' }}
                >
                  Zatwierdz
                </button>
              )}
              {detailEntry.status !== 'rejected' && (
                <button
                  type="button"
                  disabled={actionLoading}
                  onClick={() => {
                    setRejectId(detailEntry._id);
                    setRejectNote('');
                    setDetailEntry(null);
                  }}
                  style={{ ...btnStyle, background: '#fee2e2', color: '#991b1b', padding: '8px 18px', fontSize: '13px' }}
                >
                  Odrzuc
                </button>
              )}
              <button
                type="button"
                onClick={() => setDetailEntry(null)}
                style={{ ...btnStyle, background: '#f1f5f9', color: '#475569', padding: '8px 18px', fontSize: '13px' }}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && data && (
        <>
          {/* Summary header with payroll toggle */}
          {(summaryByAssistant.length > 0 || summaryByPlayer.length > 0) && (
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
                Podsumowanie
              </span>
              <button
                type="button"
                onClick={() => setApprovedOnly(v => !v)}
                style={{
                  padding: '4px 12px',
                  borderRadius: '6px',
                  border: `1px solid ${approvedOnly ? '#166534' : '#cbd5e1'}`,
                  background: approvedOnly ? '#dcfce7' : '#f8fafc',
                  color: approvedOnly ? '#166534' : '#475569',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {approvedOnly ? '✓ Tylko zatwierdzone (payroll)' : 'Wszystkie wpisy'}
              </button>
              {approvedOnly && (
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Widok do wypłat — uwzględnione tylko zatwierdzone godziny
                </span>
              )}
            </div>
          )}

          {/* Summary — per assistant */}
          {summaryByAssistant.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#64748b',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                Per asystentka
              </div>
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <table
                  style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}
                >
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={thStyle}>Asystentka</th>
                      <th style={thStyle}>Czas</th>
                      <th style={thStyle}>Koszt</th>
                      <th style={thStyle}>Wpisów</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryByAssistant.map(row => (
                      <tr key={row._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={tdStyle}>{row._id}</td>
                        <td style={tdStyle}>{formatDuration(row.totalMinutes)}</td>
                        <td style={{ ...tdStyle, color: '#16a34a', fontWeight: 600 }}>
                          {(row.totalPay || 0).toFixed(2)} PLN
                        </td>
                        <td style={{ ...tdStyle, color: '#64748b' }}>{row.entryCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Summary — per player */}
          {summaryByPlayer.length > 0 && (
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#64748b',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}
              >
                Per zawodnik
              </div>
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}
              >
                <table
                  style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}
                >
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      <th style={thStyle}>Zawodnik</th>
                      <th style={thStyle}>Czas</th>
                      <th style={thStyle}>Koszt</th>
                      <th style={thStyle}>Wpisów</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryByPlayer.map(row => (
                      <tr key={row._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={tdStyle}>{row._id}</td>
                        <td style={tdStyle}>{formatDuration(row.totalMinutes)}</td>
                        <td style={{ ...tdStyle, color: '#16a34a', fontWeight: 600 }}>
                          {(row.totalPay || 0).toFixed(2)} PLN
                        </td>
                        <td style={{ ...tdStyle, color: '#64748b' }}>{row.entryCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Entries table */}
          <div>
            {/* Table toolbar */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '10px',
                flexWrap: 'wrap',
                gap: '10px',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
                Wpisy ({data.entries.length})
              </div>

              {/* Bulk approve controls */}
              {approvableEntries.length > 0 && (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {selectedIds.size > 0 && (
                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                      Zaznaczono: {selectedIds.size}
                    </span>
                  )}
                  <button
                    type="button"
                    disabled={actionLoading || selectedIds.size === 0}
                    onClick={handleBulkApprove}
                    style={{
                      ...btnStyle,
                      padding: '7px 16px',
                      background: selectedIds.size > 0 ? '#166534' : '#f1f5f9',
                      color: selectedIds.size > 0 ? '#fff' : '#94a3b8',
                      fontSize: '13px',
                      cursor: selectedIds.size > 0 ? 'pointer' : 'not-allowed',
                      opacity: actionLoading ? 0.6 : 1,
                    }}
                  >
                    ✓ Zatwierdź zaznaczone
                    {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                  </button>
                </div>
              )}
            </div>

            <div
              style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                overflow: 'hidden',
              }}
            >
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}
                >
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                      {/* Checkbox all */}
                      <th style={{ ...thStyle, width: '36px', padding: '10px 8px 10px 14px' }}>
                        {approvableEntries.length > 0 && (
                          <input
                            type="checkbox"
                            checked={allApprovableSelected}
                            onChange={toggleSelectAll}
                            style={{ cursor: 'pointer' }}
                            title="Zaznacz wszystkie do zatwierdzenia"
                          />
                        )}
                      </th>
                      <th style={thStyle}>Asystentka</th>
                      <th style={thStyle}>Zawodnik</th>
                      <th style={thStyle}>Data</th>
                      <th style={thStyle}>Godziny</th>
                      <th style={thStyle}>Czas</th>
                      <th style={thStyle}>Stawka</th>
                      <th style={thStyle}>Koszt</th>
                      <th style={thStyle}>Notatki</th>
                      <th style={thStyle}>Status</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.entries.length === 0 && (
                      <tr>
                        <td
                          colSpan={11}
                          style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}
                        >
                          Brak wpisów dla wybranych filtrów.
                        </td>
                      </tr>
                    )}
                    {data.entries.map(entry => (
                      <React.Fragment key={entry._id}>
                        <tr
                          style={{
                            borderBottom: rejectId === entry._id ? 'none' : '1px solid #f1f5f9',
                            background: selectedIds.has(entry._id) ? '#f0fdf4' : undefined,
                            cursor: 'pointer',
                          }}
                          onClick={() => setDetailEntry(entry)}
                        >
                          {/* Row checkbox */}
                          <td
                            style={{ ...tdStyle, padding: '10px 8px 10px 14px', width: '36px' }}
                            onClick={e => e.stopPropagation()}
                          >
                            {entry.status !== 'approved' && (
                              <input
                                type="checkbox"
                                checked={selectedIds.has(entry._id)}
                                onChange={() => toggleSelect(entry._id)}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                          </td>
                          <td style={{ ...tdStyle, color: '#475569' }}>
                            {entry.assistantEmail}
                          </td>
                          <td style={{ ...tdStyle, fontWeight: 500, color: '#1e293b' }}>
                            {entry.playerName}
                          </td>
                          <td style={tdStyle}>{entry.date}</td>
                          <td style={{ ...tdStyle, color: '#64748b' }}>
                            {entry.startTime} – {entry.endTime}
                          </td>
                          <td style={tdStyle}>{formatDuration(entry.durationMinutes)}</td>
                          <td style={{ ...tdStyle, color: '#64748b' }}>
                            {entry.hourlyRate != null
                              ? `${entry.hourlyRate} PLN/h`
                              : '—'}
                          </td>
                          <td style={{ ...tdStyle, color: '#16a34a', fontWeight: 600 }}>
                            {entry.totalPay != null
                              ? `${entry.totalPay.toFixed(2)} PLN`
                              : '—'}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              color: '#64748b',
                              maxWidth: '180px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {entry.notes || <span style={{ color: '#cbd5e1' }}>—</span>}
                          </td>
                          <td style={tdStyle}>
                            <StatusBadge status={entry.status} />
                            {entry.status === 'rejected' && entry.adminNote && (
                              <div style={{ fontSize: '11px', color: '#991b1b', marginTop: '3px' }}>
                                {entry.adminNote}
                              </div>
                            )}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'right',
                              whiteSpace: 'nowrap',
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            {entry.status !== 'approved' && (
                              <button
                                type="button"
                                disabled={actionLoading}
                                onClick={() => handleApprove(entry._id)}
                                style={{
                                  ...btnStyle,
                                  background: '#dcfce7',
                                  color: '#166534',
                                  marginRight: '4px',
                                }}
                              >
                                ✓ Zatwierdź
                              </button>
                            )}
                            {entry.status !== 'rejected' && (
                              <button
                                type="button"
                                disabled={actionLoading}
                                onClick={() => {
                                  setRejectId(entry._id);
                                  setRejectNote('');
                                }}
                                style={{
                                  ...btnStyle,
                                  background: '#fee2e2',
                                  color: '#991b1b',
                                }}
                              >
                                ✗ Odrzuć
                              </button>
                            )}
                          </td>
                        </tr>

                        {/* Reject inline form */}
                        {rejectId === entry._id && (
                          <tr
                            style={{
                              borderBottom: '1px solid #f1f5f9',
                              background: '#fff7ed',
                            }}
                          >
                            <td colSpan={11} style={{ padding: '10px 16px' }}>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: '8px',
                                  alignItems: 'center',
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: '13px',
                                    color: '#92400e',
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  Powód odrzucenia:
                                </span>
                                <input
                                  type="text"
                                  placeholder="Opcjonalna notatka dla asystentki..."
                                  value={rejectNote}
                                  onChange={e => setRejectNote(e.target.value)}
                                  style={{
                                    flex: 1,
                                    padding: '6px 10px',
                                    border: '1px solid #fcd34d',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                  }}
                                />
                                <button
                                  type="button"
                                  disabled={actionLoading}
                                  onClick={() => handleRejectSubmit(entry._id)}
                                  style={{
                                    ...btnStyle,
                                    background: '#dc2626',
                                    color: '#fff',
                                    padding: '6px 14px',
                                  }}
                                >
                                  Potwierdź odrzucenie
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setRejectId(null);
                                    setRejectNote('');
                                  }}
                                  style={{
                                    ...btnStyle,
                                    background: '#f1f5f9',
                                    color: '#475569',
                                    padding: '6px 14px',
                                  }}
                                >
                                  Anuluj
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminTimeEntries;
